if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // ── Left-thumbnail layout: sync strip height to viewer, fix scroll axis ──
        if (this.elements.thumbnails.classList.contains('thumbnail-slider--left')) {
          this.initLeftThumbnails();
        }
      }

      // ── New: height sync + vertical scroll setup for left layout ────────────
      initLeftThumbnails() {
        const viewer = this.elements.viewer;
        const thumbStrip = this.elements.thumbnails;

        const syncHeight = () => {
          if (!this.mql.matches) {
            // Mobile: clear any inline height so normal flow takes over
            thumbStrip.style.height = '';
            return;
          }
          const h = viewer.offsetHeight;
          if (h > 0) thumbStrip.style.height = h + 'px';
        };

        // Run on load and whenever the viewer resizes (image swap, window resize)
        window.addEventListener('load', syncHeight);
        window.addEventListener('resize', syncHeight);

        if (typeof ResizeObserver !== 'undefined') {
          new ResizeObserver(syncHeight).observe(viewer);
        } else {
          // Fallback for older browsers
          syncHeight();
        }

        // Run once immediately in case everything is already painted
        syncHeight();
      }

      // ── Patched: scrolls the thumbnail list vertically when in left layout ──
      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const isLeft = this.elements.thumbnails.classList.contains('thumbnail-slider--left');

        if (isLeft && this.mql.matches) {
          // Vertical layout: scroll the <ul> list so the active thumbnail is visible
          const list = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (!list) return;

          const listTop = list.getBoundingClientRect().top;
          const thumbTop = thumbnail.getBoundingClientRect().top;
          const thumbBottom = thumbnail.getBoundingClientRect().bottom;
          const listBottom = list.getBoundingClientRect().bottom;

          // Only scroll if the thumbnail is outside the visible area of the list
          const isVisible = thumbTop >= listTop && thumbBottom <= listBottom;
          if (!isVisible) {
            // Scroll so the active thumbnail sits at the top of the list
            list.scrollTo({
              top: list.scrollTop + (thumbTop - listTop),
              behavior: 'smooth',
            });
          }
        } else {
          // Original horizontal behaviour for all other layouts
          if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}